/**
 * Screener (migrada do Streamlit; equivalência em backend/tests_paginas/test_screener.py).
 *
 * Na URL: ADTV mínimo, métricas e componentes de cada score (descritores separados por
 * vírgula; ausente = padrão do legado; "-" = nenhum) e a seleção da evolução de métricas.
 */
import { Alert, Loader, MultiSelect, NumberInput, Select, Text } from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { ErroApi, apiGet, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import { BarraFiltros, CampoData, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { Grafico, type GraficoApi } from '../../components/Grafico';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { useFiltrosUrl } from '../../lib/url-state';

interface Fator {
  nome: string;
  alias: string;
  descricao: string;
  estilos: string[];
}

interface DadosFatores {
  fatores: Fator[];
  padrao: Record<string, string[]>;
}

interface DadosScreener {
  escolhas: Record<string, string[]>;
  tabela: TabelaApi;
  avisos: string[];
  ativos: string[];
}

type DadosEvolucao = { aviso: string } | { grafico: GraficoApi };

export const LISTAS = ['metricas', 'momentum', 'value', 'liquidity', 'risk', 'quality'] as const;
type Lista = (typeof LISTAS)[number];

const ROTULOS: Record<Lista, string> = {
  metricas: 'Métricas exibidas',
  momentum: 'Momentum',
  value: 'Value',
  liquidity: 'Liquidity',
  risk: 'Risk',
  quality: 'Quality',
};

/** Valor da URL → parâmetro da API: "" = padrão (não envia); "-" = nenhum (vai como "-"). */
export function paraApi(valor: string): string | undefined {
  return valor === '' ? undefined : valor;
}

/** Seleção → valor da URL (igual ao padrão sai da URL; vazia vira "-"). */
export function paraUrl(escolhidos: string[], padrao: string[]): string {
  if (escolhidos.join(',') === padrao.join(',')) return '';
  return escolhidos.length ? escolhidos.join(',') : '-';
}

function mensagem(erro: unknown, padrao: string) {
  return erro instanceof ErroApi ? erro.message : padrao;
}

export function Screener() {
  const [filtros, definir] = useFiltrosUrl({
    adtv: 8000000,
    metricas: '',
    momentum: '',
    value: '',
    liquidity: '',
    risk: '',
    quality: '',
    metrica: '',
    ativos: '',
    inicio: '2010-01-01',
  });

  const fatores = useQuery({
    queryKey: ['equities.screener.fatores'],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosFatores>>('/equities/screener/fatores', {}, signal),
    staleTime: 60 * 60 * 1000,
  });
  const opcoes = (fatores.data?.data.fatores ?? []).map((f) => ({ value: f.nome, label: f.alias }));
  const padrao = fatores.data?.data.padrao ?? {};

  const parametros = Object.fromEntries(LISTAS.map((l) => [l, paraApi(filtros[l])]));
  const tabela = useQuery({
    queryKey: ['equities.screener', filtros.adtv, parametros],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosScreener>>(
        '/equities/screener',
        { adtv: filtros.adtv, ...parametros },
        signal,
      ),
    placeholderData: keepPreviousData,
  });
  const dados = tabela.data?.data;

  const metrica = filtros.metrica || opcoes[0]?.value || '';
  const ativos = filtros.ativos ? filtros.ativos.split(',') : [];
  const evolucao = useQuery({
    queryKey: ['equities.screener.evolucao', metrica, filtros.ativos, filtros.inicio],
    queryFn: ({ signal }) =>
      apiGet<Envelope<DadosEvolucao>>(
        '/equities/screener/evolucao',
        { metrica, ativos: filtros.ativos, inicio: filtros.inicio },
        signal,
      ),
    enabled: !!metrica && ativos.length > 0,
    placeholderData: keepPreviousData,
  });

  // Seleção efetiva de uma lista: a da URL ou o padrão do legado.
  const escolhidos = (lista: Lista) =>
    dados?.escolhas[lista] ??
    (filtros[lista] === '-'
      ? []
      : filtros[lista]
        ? filtros[lista].split(',')
        : (padrao[lista] ?? []));

  const seletor = (lista: Lista) => (
    <MultiSelect
      key={lista}
      label={ROTULOS[lista]}
      searchable
      clearable
      disabled={!fatores.data}
      data={opcoes}
      value={escolhidos(lista)}
      onChange={(v) => definir({ [lista]: paraUrl(v, padrao[lista] ?? []) })}
      w={lista === 'metricas' ? 360 : LARGURA.selecao}
      // Muitas escolhas: a caixa rola por dentro em vez de crescer.
      styles={{ pillsList: { maxHeight: 64, overflowY: 'auto' } }}
    />
  );

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Screener"
        descricao="Ações em reais com os descritores escolhidos e cinco scores fatoriais: Momentum, Value, Liquidity, Risk e Quality."
        fonte="Banco Persevera (factor_zoo) · Fibery (fatores e ações ativas)"
      />
      <BarraFiltros carregando={fatores.isFetching || tabela.isFetching || evolucao.isFetching}>
        <GrupoFiltros titulo="Universo">
          <NumberInput
            label="ADTV mínimo (21d)"
            min={0}
            step={500000}
            thousandSeparator="."
            decimalSeparator=","
            value={filtros.adtv}
            onChange={(v) => typeof v === 'number' && v >= 0 && definir({ adtv: v })}
            w={LARGURA.texto}
          />
          {seletor('metricas')}
        </GrupoFiltros>
        <GrupoFiltros titulo="Componentes dos scores">
          {(['momentum', 'value', 'liquidity', 'risk', 'quality'] as const).map(seletor)}
        </GrupoFiltros>
        <GrupoFiltros titulo="Evolução de métricas">
          <Select
            label="Métrica"
            searchable
            allowDeselect={false}
            disabled={!fatores.data}
            data={opcoes}
            value={metrica || null}
            onChange={(m) => m && definir({ metrica: m })}
            w={LARGURA.selecao}
          />
          <MultiSelect
            label="Ativos"
            searchable
            clearable
            disabled={!dados}
            data={dados?.ativos ?? []}
            value={ativos}
            onChange={(v) => definir({ ativos: v.join(',') })}
            w={LARGURA.selecao}
          />
          <CampoData
            rotulo="Data inicial"
            valor={filtros.inicio}
            min="2010-01-01"
            aoMudar={(inicio) => definir({ inicio })}
          />
        </GrupoFiltros>
      </BarraFiltros>

      {fatores.isError && (
        <Alert color="red">{mensagem(fatores.error, 'Falha ao carregar os fatores.')}</Alert>
      )}
      {tabela.isError && (
        <Alert color="red">{mensagem(tabela.error, 'Não foi possível carregar os dados.')}</Alert>
      )}
      {tabela.isPending && <Loader />}
      {dados && (
        <>
          <AvisosApi avisos={[dados.avisos, tabela.data?.meta.avisos]} />
          <Secao
            titulo={`Ações (${dados.tabela.linhas.length})`}
            legenda="Scores em desvios-padrão (z-score médio dos componentes; maior é melhor), coloridos por quartil. Métricas em verde/vermelho pelo sinal."
          >
            <TabelaDados tabela={dados.tabela} altura={560} />
          </Secao>
        </>
      )}

      <Secao
        titulo="Evolução de métricas"
        legenda="Histórico de um descritor para os ativos escolhidos no grupo Evolução de métricas da barra de filtros."
      >
        {ativos.length === 0 ? (
          <Text size="sm" c="dimmed">
            Escolha a métrica e os ativos na barra de filtros (grupo Evolução de métricas).
          </Text>
        ) : evolucao.isError ? (
          <Alert color="red">{mensagem(evolucao.error, 'Erro ao carregar a série.')}</Alert>
        ) : !evolucao.data ? (
          <Loader />
        ) : 'aviso' in evolucao.data.data ? (
          <Alert color="yellow">{evolucao.data.data.aviso}</Alert>
        ) : (
          <Grafico grafico={evolucao.data.data.grafico} />
        )}
      </Secao>
    </Pagina>
  );
}
